"use client";

import { useEffect, useRef } from "react";
import { startSsoProbe } from "@/lib/sso/probe";

/**
 * Wykrywanie zalogowania w panelu na JUŻ OTWARTEJ karcie frontu.
 *
 * **Problem:** `<SsoProbe>` obsługuje wejście na front. Gdy jednak karta z
 * frontem zostaje otwarta, a użytkownik loguje się w międzyczasie w panelu
 * IADMIN, nic nie każe tej karcie zmienić stanu - front nadal pokazuje anonima
 * aż do odświeżenia. Backend nie ma jak jej o tym powiedzieć: cokolwiek
 * wysłanego w tle na obcy host to zapis third-party cookie, blokowany przez
 * przeglądarki. Skoro nie da się PCHNĄĆ, karta musi sama ZAPYTAĆ.
 *
 * **Mechanizm:** zapytanie do `GET <backend>/ISSO/state` z `credentials: "include"`.
 * Endpoint rozpoznaje sesję po cookie panelu i odpowiada jednym bool-em - bez
 * tokenu i bez danych użytkownika, więc nie wymaga klucza API (ten jest sekretem
 * serwera frontu i nie może trafić do przeglądarki). Gdy odpowie `logged_in: true`,
 * uruchamiamy zwykły probe - sesja frontu musi powstać jako cookie httpOnly, więc
 * ustawia ją serwer w odpowiedzi na nawigację, a nie JavaScript.
 *
 * **Warunek działania:** cookie panelu ma `SameSite=Lax`, więc jedzie z tym
 * zapytaniem tylko gdy front i backend są w tej samej domenie rejestrowalnej
 * (np. `cms.firma.pl` i `panel.firma.pl`). Dla frontu na zupełnie innej domenie
 * przeglądarka cookie nie wyśle i wykrywanie w tle jest niemożliwe - dlatego
 * `PublicLayout` w ogóle nie renderuje tego komponentu w takim układzie, a sesję
 * przekazuje wtedy wejście na front albo przejście z panelu (`/ISSO/goto`).
 *
 * **Koszt:** zapytanie wykonuje się przy przejęciu focusu przez kartę (to pokrywa
 * scenariusz "zaloguj się w panelu, wróć na front") oraz rzadkim pollingiem
 * wyłącznie gdy karta jest widoczna. Licznik ogranicza odpytywanie porzuconej
 * karty; powrót focusu zeruje go, więc realny użytkownik nigdy nie traci wykrywania.
 */

/** Odstęp między zapytaniami w tle (ms) - rzadko, bo focus i tak pokrywa realny scenariusz. */
const POLL_INTERVAL_MS = 60_000;

/** Minimalny odstęp między zapytaniami (ms) - dławi serie zdarzeń focus/visibility. */
const MIN_GAP_MS = 5_000;

/** Ile zapytań w tle wykonać zanim uznamy kartę za porzuconą (focus zeruje licznik). */
const MAX_BACKGROUND_CHECKS = 30;

type SsoStateResponse = {
  logged_in?: boolean;
};

export function AdminSessionPoll({ apiBase }: { apiBase: string }) {
  /* Refy zamiast stanu - zmiany nie mają nic re-renderować, a callbacki
     interwału i listenerów muszą widzieć aktualne wartości. */
  const lastCheckRef = useRef(0);
  const checksLeftRef = useRef(MAX_BACKGROUND_CHECKS);
  const warnedRef = useRef(false);

  useEffect(() => {
    if (!apiBase || typeof window === "undefined") return;

    let cancelled = false;
    const endpoint = apiBase.replace(/\/$/, "") + "/ISSO/state";

    const check = async (): Promise<void> => {
      if (cancelled || document.hidden) return;

      const now = Date.now();
      if (now - lastCheckRef.current < MIN_GAP_MS) return;
      lastCheckRef.current = now;

      try {
        /* Bez własnych nagłówków - dzięki temu zapytanie pozostaje "proste"
           w rozumieniu CORS i nie wymaga preflightu. */
        const response = await fetch(endpoint, {
          credentials: "include",
          cache: "no-store",
        });

        if (!response.ok) return;

        const payload = (await response.json()) as SsoStateResponse;

        if (cancelled || payload?.logged_in !== true) return;

        startSsoProbe(apiBase);
      } catch (e) {
        /* Najczęściej brak sieci albo backend chwilowo nieosiągalny. Logujemy
           RAZ - powtarzanie przy każdym cyklu zasypałoby konsolę. */
        if (!warnedRef.current) {
          warnedRef.current = true;
          console.warn(
            `[AdminSessionPoll] sprawdzenie sesji panelu nie powiodło się: ${e instanceof Error ? e.message : String(e)}`,
          );
        }
      }
    };

    /* Powrót na kartę to najczęstszy moment, w którym zalogowanie właśnie
       nastąpiło (user był w panelu w innej karcie) - stąd natychmiastowe
       sprawdzenie i reset licznika zapytań w tle. */
    const onFocus = (): void => {
      checksLeftRef.current = MAX_BACKGROUND_CHECKS;
      void check();
    };

    const onVisibility = (): void => {
      if (!document.hidden) onFocus();
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    const timer = window.setInterval(() => {
      if (document.hidden || checksLeftRef.current <= 0) return;
      checksLeftRef.current -= 1;
      void check();
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [apiBase]);

  return null;
}
